import React, { useState, useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { updateWidgetEditorState, updateWidgetProperty } from "actions/editor";

import _ from "lodash";
import cx from "classnames";
import { Button, FormCheck, Tab, Tabs } from "react-bootstrap";
import Tooltip from "components/Tooltip";
import { Property } from "../properties/";
import { ANIMATION_TYPES, EXIT_ANIMATION_TYPES } from "constants/animationTypes";

import { Widgets } from "@paperflite-marketing/cleverstory-commons";

import styles from "./animation.module.scss";
import { useWidgetSelected } from "components/Editor/hooks/useEditorSession";
import { EditorContext } from "components/Editor";
import { HOTSPOT } from "constants/widgetTypes";

export const AnimationProperty = ({
  id,
  widget,
  position,
  editorContext,
  name,
  exclusions = [],
  ...rest
}) => {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    animationType: "none",
    widgetId: id,
    customize: false,
    activeTab: "entry",
    animating: false,
  });

  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector((state) =>
    _.get(state, `${editorSession}.present.breakpoint`)
  );

  const animation =
  _.get(widget, `properties.${breakpoint}.animation`, {}) || {};
  const animationType = _.get(animation, "type", "none");

  const preview = _.get(widget, `editorState.previewAnimation`);

  const storyboardType = useSelector((state) =>
    _.get(state, `storyboards.storyboard.item.type`)
  );

  useEffect(() => {
    if (id !== state.widgetId) {
      handleClose();
    }
  }, [id]);

  const handleAnimation = (value, field) => {
    const previewType = _.get(preview, "type");

    if (previewType != animationType) {
      dispatch(
        updateWidgetEditorState(editorSession, widget.uid, {
          previewAnimation: null,
        })
      );
    }

    dispatch(
      updateWidgetProperty(editorSession, widget.uid, breakpoint, {
        [field]: value,
      })
    );

    setState((state) => {
      return {
        ...state,
        customize: !_.isEmpty(value),
      };
    });
  };

  const handleTabChange = (key) => {
    setState(() => {
      return {
        ...state,
        activeTab: key,
      };
    });
  };

  const handlePreview = (value) => {
    if (!value || value.type === "parallax") {
      resetPreview();
      return;
    }

    resetPreview();

    window.setTimeout(() => {
      dispatch(
        updateWidgetEditorState(editorSession, widget.uid, {
          previewAnimation: value,
        })
      );
    }, [500]);
  };

  const resetPreview = () => {
    dispatch(
      updateWidgetEditorState(editorSession, widget.uid, {
        previewAnimation: null,
      })
    );

    setState((state) => {
      return {
        ...state,
        animating: false,
      };
    });
  };

  const handleBackClicked = () => {
    setState((state) => {
      return {
        ...state,
        customize: false,
      };
    });
  };

  const handleClose = () => {
    rest.onCancel();
  };

  useEffect(() => {
    if (_.isEqual(preview, animation) || !state.animating) {
      return;
    }

    dispatch(
      updateWidgetEditorState(editorSession, widget.uid, {
        previewAnimation: animation,
      })
    );
  }, [animation]);

  return (
    <Property.Dialog
      className={styles.animationDialog}
      width={250}
      height={"auto"}
      target={`#${id}`}
    >
      <Property.Header
        className={styles.animationHeader}
        title={state.customize ? "Settings" : "Animations"}
      >
        {state.customize && (
          <Button
            variant="info"
            className={styles.backButton}
            onClick={() => handleBackClicked()}
          >
            <i className={cx("cleverstory-drop-down")} />
          </Button>
        )}
        <Button className={styles.action} onClick={handleClose}>
          <i className="cleverstory-close" />
        </Button>
      </Property.Header>
      {widget.type !== HOTSPOT && (
        <Property.Body className={styles.dialogBody}>
          {!state.customize && (
            <div className={styles.animationContainer}>
              <h5>Choose a style</h5>
              <div className={styles.grid}>
                {ANIMATION_TYPES.filter(
                  (item) => !exclusions.includes(item.type) && !item.exit
                ).map((item, index) => {
                  const active = animationType === item.type;
                  var value = animation;
                  if (!active) {
                    value = item.defaultAnimation;
                  }

                  if (storyboardType == "SPATIAL" && item.type === "parallax") {
                    return null;
                  }

                  return (
                    <div
                      key={index}
                      active={active}
                      className={cx(styles.animationTypeButton, {
                        [styles.active]: active,
                      })}
                      onMouseEnter={() => handlePreview(value)}
                      onMouseLeave={() => resetPreview()}
                      onClick={() => handleAnimation(value, 'animation')}
                    >
                      <div className={cx(styles.icon, {
                         [styles.biggerIcon]: item.type === "expand" || item.type === "puffin",
                         })}
                      >                        
                         <img src={item.buttonImg} />
                      </div>
                      <span>{item.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {state.customize && (
            <AnimationCustomizeSettings
              editorContext={editorContext}
              widget={widget}
              handlePreview={handlePreview}
              resetPreview={resetPreview}
              state={state}
              setState={setState}
            />
          )}
        </Property.Body>
      )}
      {widget.type === HOTSPOT && (
        <Property.Body className={styles.dialogBody}>
          {!state.customize && (
            <HotSpotAnimationSettings
              widget={widget}
              breakpoint={breakpoint}
              activeTab={state.activeTab}
              storyboardType={storyboardType}
              handleTabChange={handleTabChange}
              exclusions={exclusions}
              handlePreview={handlePreview}
              resetPreview={resetPreview}
              handleAnimation={handleAnimation}
            />
          )}
          {state.customize && (
            <AnimationCustomizeSettings
              state={state}
              exit={state.activeTab === 'entry' ? false : true}
              setState={setState}
              widget={widget}
              handlePreview={handlePreview}
              resetPreview={resetPreview}
              editorContext={editorContext}
            />
          )}
        </Property.Body>
      )}
    </Property.Dialog>
  );
};

const AnimationCustomizeSettings = ({resetPreview, editorContext, state, setState, widget, handlePreview, exit=false}) => {
  const dispatch = useDispatch();


  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector((state) =>
    _.get(state, `${editorSession}.present.breakpoint`)
  );
  const animation = exit ? _.get(widget, `properties.${breakpoint}.exitAnimation`, {}) || {} : _.get(widget, `properties.${breakpoint}.animation`, {}) || {}
  const animationType = _.get(animation, "type", "none");

  const preview = _.get(widget, `editorState.previewAnimation`);

  const animationConfiguration =
    ANIMATION_TYPES.find((data) => data.type === animationType) || {};
  const directionOptions = _.get(animationConfiguration, "directions");

  const handlePropertyChange = (field, value) => {
    if(exit) {
      dispatch(
        updateWidgetProperty(editorSession, widget.uid, breakpoint, {
          exitAnimation: {
            ...animation,
            [field]: value,
          },
        })
      );
      return;
    }

    dispatch(
      updateWidgetProperty(editorSession, widget.uid, breakpoint, {
        animation: {
          ...animation,
          [field]: value,
        },
      })
    );

  };

  return (
    <div className={styles.customizeDialog}>
      <div className={styles.animationType}>
        <span className={styles.name}>
          {_.get(animationConfiguration, "name", animationType)}
        </span>
        {animation.type === "parallax" && (
          <Tooltip placement="top" content={"Scroll to preview animation"}>
            <div>
              <Property.Switch
                id="parallax"
                value={preview != null && state.animating}
                onChange={(data) => {
                  if (data && !state.animating) {
                    setState((state) => {
                      return {
                        ...state,
                        animating: true,
                      };
                    });
                    dispatch(
                      updateWidgetEditorState(editorSession, widget.uid, {
                        previewAnimation: animation,
                      })
                    );
                  } else {
                    resetPreview();
                  }
                }}
              />
            </div>
          </Tooltip>
        )}
        {animation.type !== "parallax" && (
          <Button
            variant="info"
            onClick={() => {
              let animationData = animation;
              if (exit) {
                animationData = {
                ...animationData,
                exit: true,
                };
              }
              handlePreview(animationData);
            }}
            className={styles.previewButton}
          >
            <i className={cx("cleverstory-play")} />
          </Button>
        )}
      </div>
      {directionOptions && (
        <Property name="Direction">
          <div className={styles.animationDirection}>
            <div className={styles.options}>
              {directionOptions.map((item, index) => (
                <button
                  key={index}
                  className={cx(styles.directions, {
                    [styles.active]: animation.direction === item.value,
                  })}
                  onClick={() => {
                    handlePropertyChange("direction", _.get(item, "value"));
                  }}
                >
                  <i className={cx(`cleverstory-${item.icon}`)}></i>
                </button>
              ))}
              {animationType === "parallax" && (
                <button
                  className={cx(styles.directions, {
                    [styles.active]: animation.direction === "angle",
                  })}
                  onClick={() => {
                    handlePropertyChange("direction", "angle");
                  }}
                >
                  <i className={cx(`cleverstory-degree`)}></i>
                </button>
              )}
            </div>
          </div>
        </Property>
      )}
      {animationType === "slide" && (
        <Property name="Distance" className={styles.animationDurationProperty}>
          <Property.Slider
            input
            min={5}
            className={styles.customSlider}
            max={300}
            step={1}
            value={animation.distance}
            onChange={(data) => handlePropertyChange("distance", data)}
          />
        </Property>
      )}
      {animationType === "spin" && (
        <Property name="Spins" className={styles.animationDurationProperty}>
          <Property.Slider
            input
            min={1}
            className={styles.customSlider}
            max={15}
            step={1}
            value={animation.count}
            onChange={(data) => handlePropertyChange("count", data)}
          />
        </Property>
      )}
      {animationType !== "parallax" && (
        <>
          <Property name="Duration">
            <Property.Slider
              input
              min={0.1}
              max={5}
              step={0.1}
              value={animation.duration}
              onChange={(data) => handlePropertyChange("duration", data)}
            />
          </Property>
          <Property name="Delay">
            <Property.Slider
              input
              min={0}
              max={10}
              step={0.1}
              value={animation.delay}
              onChange={(data) => handlePropertyChange("delay", data)}
            />
          </Property>

          <Property>
            <Property.Switch
              id="animate"
              title={"Only animate first time"}
              value={animation.triggerOnce}
              onChange={() =>
                handlePropertyChange("triggerOnce", !animation.triggerOnce)
              }
            />
          </Property>
        </>
      )}
      {animationType === "parallax" && animation.direction === "angle" && (
        <Property name="Degree" className={styles.rotationControlProperty}>
          <Property.Rotation
            input
            min={0}
            max={360}
            step={1}
            value={animation.angle}
            onChange={(data) => handlePropertyChange("angle", data)}
          />
        </Property>
      )}
      {animationType === "parallax" && (
        <>
          <Property name="Speed" className={styles.animationDurationProperty}>
            <Property.Slider
              input
              min={1}
              className={styles.customSlider}
              max={5}
              step={1}
              value={animation.speed}
              onChange={(data) => handlePropertyChange("speed", parseInt(data))}
            />
          </Property>
          <Property
            name="Assign current position of element as"
            className={styles.animationPositionProperty}
          >
            <Property.RadioButton
              className={styles.radio}
              title="Start position"
              value={animation.reverse}
              onClick={() => handlePropertyChange("reverse", true)}
            />
            <Property.RadioButton
              className={styles.radio}
              title="End position"
              value={!animation.reverse}
              onClick={() => handlePropertyChange("reverse", false)}
            />
          </Property>
        </>
      )}
    </div>
  );
};

const HotSpotAnimationSettings = ({
  exclusions,
  handleAnimation,
  handleTabChange,
  breakpoint,
  activeTab,
  handlePreview,
  resetPreview,
  widget,
  storyboardType,
}) => {

  const animation = activeTab === 'entry' ? _.get(widget, `properties.${breakpoint}.animation`, {}) || {} : _.get(widget, `properties.${breakpoint}.exitAnimation`, {}) || {}
  const animationType = _.get(animation, "type", "none");

  return (
    <Tabs
      activeKey={activeTab}
      onSelect={handleTabChange}
      className={styles.buttonStyleTabs}
    >
      <Tab
        eventKey="entry"
        title="Entry"
        className={styles.buttonStyleTab}
      >
        <div className={styles.animationContainer}>
          <h5>Choose a style</h5>
          <div className={styles.grid}>
            {ANIMATION_TYPES.filter(
              (item) => !exclusions.includes(item.type)
            ).map((item, index) => {
              const active = animationType === item.type;
              var value = animation;
              if (!active) {
                value = item.defaultAnimation;
              }

              if (storyboardType == "SPATIAL" && item.type === "parallax") {
                return null;
              }

              return (
                <div
                  key={index}
                  active={active}
                  className={cx(styles.animationTypeButton, {
                    [styles.active]: active,
                  })}
                  onMouseEnter={() => handlePreview(value)}
                  onMouseLeave={() => resetPreview()}
                  onClick={() => handleAnimation(value, 'animation')}
                >
                <div className={cx(styles.icon, {
                  [styles.biggerIcon]: item.type === "expand" || item.type === "puffin",
                  })}
                >
                    <img src={item.buttonImg} />
                  </div>
                  <span>{item.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Tab>
      <Tab
        eventKey="exit"
        title="Exit"
        className={styles.buttonStyleTab}
      >
        <div className={styles.animationContainer}>
          <h5>Choose a style</h5>
          <div className={styles.grid}>
            {EXIT_ANIMATION_TYPES.filter(
              (item) => ![...exclusions].includes(item.type) 
            ).map((item, index) => {
              const active = animationType === item.type;
              var value = animation;
              if (!active) {
                value = item.defaultAnimation;
              }

              if (storyboardType == "SPATIAL" && item.type === "parallax") {
                return null;
              }

              return (
                <div
                  key={index}
                  active={active}
                  className={cx(styles.animationTypeButton, {
                    [styles.active]: active,
                  })}
                  onMouseEnter={() => handlePreview({...value, exit: true})}
                  onMouseLeave={() => resetPreview()}
                  onClick={() => handleAnimation(value, 'exitAnimation')}
                >
                <div className={cx(styles.icon, {
                  [styles.biggerIcon]: item.type === "expand" || item.type === "puffin",
                  })}
                >               
                    <img src={item.buttonImg} />
                  </div>
                  <span>{item.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Tab>
    </Tabs>
  );
};

export const Animation = ({ widget, children, exit=false }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector((state) =>
    _.get(state, `${editorSession}.present.breakpoint`)
  );
  const animationData = _.get(widget, `properties.${breakpoint}.animation`);
  const previewAnimationData = _.get(widget, `editorState.previewAnimation`);

  const animationToRender = previewAnimationData
    ? previewAnimationData
    : animationData;
  const type = _.get(animationToRender, "type") || "none";
  const uid = _.get(widget, "uid");

  const selected = useWidgetSelected(editorSession, uid);
  useEffect(() => {
    if (!selected && previewAnimationData) {
      dispatch(
        updateWidgetEditorState(editorSession, uid, {
          previewAnimation: null,
        })
      );
    }
  }, [selected, previewAnimationData]);

  if (!previewAnimationData || !widget) {
    return children;
  }

  const options = {
    ...previewAnimationData,
  };

  return (
    <Widgets.Animation key={type} {...options}>
      {children}
    </Widgets.Animation>
  );
};
